/**
 * Préparation de l'écran « Clôture annuelle » : avertissement quand décembre est verrouillé seul,
 * paramètres suggérés à partir des données de l'entreprise, récapitulatif des écritures à passer.
 *
 * Le récapitulatif utilise EXACTEMENT les mêmes calculs que `cloturerExerciceAtomique` (mêmes services,
 * mêmes entrées) : ce que l'utilisateur lit est ce qui sera passé.
 */
import type { Ecriture } from '@shared/types/Ecriture';
import type { ExerciceFiscal } from '@shared/types/ExerciceFiscal';
import type { Immobilisation } from '@shared/types/Immobilisation';
import type { Bulletin } from '@shared/types/Bulletin';
import { ClotureAnnuelleService, type ClotureParams } from '@domain/exercices/ClotureAnnuelleService';
import { genererEcrituresDotation } from '@domain/comptabilite/ImmobilisationRegistre';
import { totauxPiece } from '@domain/comptabilite/EquilibrePiece';
import { roundFCFA } from '@shared/utils/round';

export const AVERTISSEMENT_DECEMBRE_MENSUEL = 'Utilisez la clôture annuelle pour décembre.';

export const GARANTIE_CLOTURE_ANNUELLE =
  'Toutes les écritures sont passées avant le verrouillage de décembre ; en cas d’échec, rien n’est verrouillé.';

const MOIS_FR = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

export function libelleMois(periode: string): string {
  const [a, m] = periode.split('-');
  const nom = MOIS_FR[Number(m) - 1];
  return nom ? `${nom} ${a}` : periode;
}

/** Exercice de l'année civile, dans la forme attendue par la clôture (le code = l'année). */
export function exerciceFiscalDepuisAnnee(
  tenantId: string,
  annee: string,
  statut: ExerciceFiscal['statut'] = 'ouvert',
  id = `exo-${annee}`,
): ExerciceFiscal {
  return {
    id,
    tenant_id: tenantId,
    code: annee,
    date_debut: `${annee}-01-01`,
    date_fin: `${annee}-12-31`,
    statut,
    cree_le: new Date(0).toISOString(),
  };
}

/** Décembre verrouillé seul (bouton mensuel) alors que la clôture annuelle n'a pas été faite. */
export function avertissementDecembreVerrouilleSeul(input: {
  annee: string;
  cloturees: readonly string[];
  exerciceCloture: boolean;
}): string | null {
  if (input.exerciceCloture) return null;
  if (!input.cloturees.includes(`${input.annee}-12`)) return null;
  return (
    `Attention : ${libelleMois(`${input.annee}-12`)} est verrouillé, mais la clôture annuelle n’a pas été faite. ` +
    'Les écritures de fin d’année (amortissements, provisions, résultat) ne peuvent plus être passées. ' +
    'Demandez à l’administrateur de déverrouiller décembre, puis utilisez « Clôture annuelle ».'
  );
}

function solde(ecritures: readonly Ecriture[], prefixe: string): number {
  let debit = 0;
  let credit = 0;
  for (const e of ecritures) {
    if (e.compte.startsWith(prefixe)) {
      debit += e.debit || 0;
      credit += e.credit || 0;
    }
  }
  return roundFCFA(debit - credit);
}

export function suggererParametresCloture(input: {
  annee: string;
  ecritures: readonly Ecriture[];
  bulletins: readonly Bulletin[];
  capitalSocial: number;
}): ClotureParams {
  const masse = input.bulletins
    .filter((b) => (b.periode || '').startsWith(`${input.annee}-`))
    .reduce((s, b) => s + (b.brut_imposable || 0), 0);
  return {
    encours_clients: Math.max(0, solde(input.ecritures, '411')),
    masse_salariale: roundFCFA(masse),
    capital_social: Math.max(0, input.capitalSocial || 0),
    reserves_existantes: Math.max(0, -solde(input.ecritures, '1110')),
  };
}

export interface LigneRecapCloture {
  readonly libelle: string;
  /** Montant principal de la ligne (FCFA), pour l'affichage. */
  readonly montant: number;
  readonly nbEcritures: number;
}

export interface RecapitulatifCloture {
  readonly dotations: Ecriture[];
  readonly provisions: Ecriture[];
  readonly resultat: Ecriture[];
  readonly resultat_net: number;
  readonly dotation_reserve_legale: number;
  readonly nbEcritures: number;
  readonly lignes: LigneRecapCloture[];
}

export function preparerClotureAnnuelle(input: {
  exercice: ExerciceFiscal;
  ecritures: readonly Ecriture[];
  immobilisations: readonly Immobilisation[];
  params: ClotureParams;
}): RecapitulatifCloture {
  const annee = Number(input.exercice.code);
  const dotations: Ecriture[] = [];
  for (const immo of input.immobilisations) {
    if (immo.cede) continue;
    const dejaPassee = input.ecritures.some(
      (e) =>
        e.source === 'amortissement' &&
        e.groupe_id === immo.id &&
        (e.periode || '').startsWith(`${input.exercice.code}-`),
    );
    if (dejaPassee) continue;
    dotations.push(...genererEcrituresDotation(immo, annee));
  }
  const r = ClotureAnnuelleService.cloturer(
    input.exercice,
    [...input.ecritures, ...dotations],
    input.params,
  );
  const sommeDebit = (l: readonly Ecriture[]): number => totauxPiece(l).debit;
  const lignes: LigneRecapCloture[] = [
    { libelle: 'Dotations aux amortissements', montant: sommeDebit(dotations), nbEcritures: dotations.length },
    { libelle: 'Provisions de fin d’exercice (créances douteuses, congés payés, indemnités de fin de carrière)', montant: sommeDebit(r.ecritures_provisions), nbEcritures: r.ecritures_provisions.length },
    { libelle: r.resultat_net >= 0 ? 'Résultat de l’exercice (bénéfice) et report à nouveau' : 'Résultat de l’exercice (perte) et report à nouveau', montant: Math.abs(r.resultat_net), nbEcritures: r.ecritures_resultat.length },
    { libelle: 'Réserve légale (dotation de l’exercice)', montant: r.dotation_reserve_legale, nbEcritures: r.dotation_reserve_legale > 0 ? 2 : 0 },
  ];
  return {
    dotations,
    provisions: r.ecritures_provisions,
    resultat: r.ecritures_resultat,
    resultat_net: r.resultat_net,
    dotation_reserve_legale: r.dotation_reserve_legale,
    nbEcritures: dotations.length + r.ecritures_provisions.length + r.ecritures_resultat.length,
    lignes,
  };
}
